import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { forkJoin, from, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { InvisibleRecaptchaService } from 'src/app/core/service/invisible-recaptcha.service';
import { computeContentMd5 } from '../utils/bio-md5.util';
import {
  BioApiErrorBody,
  BioPresignedUrlResponse,
  BioProcessImageType,
  BioProcessInitResponse,
  BioProcessStatus,
  BioSelfieCapture,
  BioValidateResponse,
} from '../model/bio-process.model';

export interface BioDocumentValidationResult {
  success: boolean;
  status?: BioProcessStatus;
  requestId?: string;
  customerBiometricId?: number | null;
  message?: string;
  errorKind?: BioErrorKind;
  errorPhase?: 'PRESIGNED_URL' | 'UPLOAD' | 'VALIDATE_PROCESS';
  title?: string;
}

export type BioErrorKind =
  | 'SESSION'
  | 'ACCESS'
  | 'REQUEST'
  | 'CAPTCHA'
  | 'PROCESS_NOT_FOUND'
  | 'PROCESS_CONFLICT'
  | 'PROCESS_EXPIRED'
  | 'TECHNICAL'
  | 'RETRY'
  | 'FINAL_FAILED'
  | 'DOCUMENT'
  | 'IMAGE'
  | 'PRESIGNED_URL_ERROR'
  | 'UPLOAD_ERROR'
  | 'VALIDATE_PROCESS_ERROR';

export abstract class BioDocumentValidationAdapter {
  abstract validateDocument(
    frontFile: File,
    backFile: File,
    transactionId: string,
  ): Observable<BioDocumentValidationResult>;
}

const BIO_ERROR_UX: Record<BioErrorKind, { title: string; message: string }> = {
  SESSION: {
    title: 'No pudimos validar tu sesión',
    message: 'Tu sesión no es válida o expiró. Volvé a ingresar e intentá nuevamente.',
  },
  ACCESS: {
    title: 'No pudimos iniciar la validación',
    message: 'No fue posible acceder al servicio de validación. Intentá nuevamente más tarde.',
  },
  REQUEST: {
    title: 'No pudimos procesar la solicitud',
    message: 'Hubo un problema al iniciar la validación. Intentá nuevamente.',
  },
  CAPTCHA: {
    title: 'Necesitamos validar que sos vos',
    message: 'Intentá nuevamente para continuar con la verificación.',
  },
  PROCESS_NOT_FOUND: {
    title: 'No encontramos la verificación',
    message: 'Vamos a iniciar nuevamente el proceso de validación.',
  },
  PROCESS_CONFLICT: {
    title: 'No pudimos continuar la verificación',
    message: 'El proceso se encuentra en un estado que no permite continuar. Intentá nuevamente.',
  },
  PROCESS_EXPIRED: {
    title: 'La verificación expiró',
    message: 'El tiempo para completar la validación biométrica finalizó. Volvé a iniciar el proceso.',
  },
  TECHNICAL: {
    title: 'No pudimos completar la verificación',
    message: 'Ocurrió un problema con el servicio de validación. Intentá nuevamente más tarde.',
  },
  RETRY: {
    title: 'No pudimos completar la validación',
    message: 'Intentá nuevamente.',
  },
  FINAL_FAILED: {
    title: 'No pudimos validar tu identidad',
    message: 'No pudimos completar la verificación con los intentos disponibles. Intentá más tarde o contactanos.',
  },
  IMAGE: {
    title: 'No se ve tu foto',
    message: 'No pudimos completar la verificación. Intentá nuevamente.',
  },
  DOCUMENT: {
    title: 'No pudimos validar el documento',
    message: 'No pudimos validar el documento. Intentá nuevamente.',
  },
  PRESIGNED_URL_ERROR: {
    title: 'No pudimos completar la validación',
    message: 'Ocurrió un problema durante la preparación de la validación. Intentá nuevamente.',
  },
  UPLOAD_ERROR: {
    title: 'No pudimos completar la validación',
    message: 'Ocurrió un problema al cargar las selfies. Intentá nuevamente.',
  },
  VALIDATE_PROCESS_ERROR: {
    title: 'No pudimos completar la validación',
    message: 'Ocurrió un problema durante la validación. Intentá nuevamente.',
  },
};

const ERROR_CODE_TO_KIND: Record<string, BioErrorKind> = {
  INVALID_REQUEST: 'REQUEST',
  INVALID_CAPTCHA: 'CAPTCHA',
  CAPTCHA_REQUIRED: 'CAPTCHA',
  UNAUTHORIZED: 'SESSION',
  PROCESS_TYPE_NOT_FOUND: 'REQUEST',
  PROCESS_NOT_FOUND: 'PROCESS_NOT_FOUND',
  PROCESS_STATE_CONFLICT: 'PROCESS_CONFLICT',
  PROCESS_EXPIRED: 'PROCESS_EXPIRED',
  INTERNAL_ERROR: 'TECHNICAL',
  PRESIGNED_URL_ERROR: 'PRESIGNED_URL_ERROR',
  UPLOAD_ERROR: 'UPLOAD_ERROR',
  VALIDATE_PROCESS_ERROR: 'VALIDATE_PROCESS_ERROR',
};

const HTTP_STATUS_TO_KIND: Record<number, BioErrorKind> = {
  400: 'REQUEST',
  401: 'SESSION',
  403: 'ACCESS',
  404: 'PROCESS_NOT_FOUND',
  409: 'PROCESS_CONFLICT',
  410: 'PROCESS_EXPIRED',
  500: 'TECHNICAL',
  502: 'TECHNICAL',
};

const FAILURE_REASON_TO_IMAGE_KIND: ReadonlySet<string> = new Set<string>([]);

@Injectable({
  providedIn: 'root',
})
export class BioDocumentValidationService implements BioDocumentValidationAdapter {
  private readonly captchaAction = 'bio_document_validation';
  private readonly bioConfig = environment.bio;
  private readonly presignedCache = new Map<string, BioPresignedUrlResponse>();

  constructor(
    private readonly http: HttpClient,
    private readonly captchaSrv: InvisibleRecaptchaService,
  ) {}

  validateDocument(frontFile: File, backFile: File, transactionId: string): Observable<BioDocumentValidationResult> {
    return this.initProcess(transactionId).pipe(
      switchMap((initRes) => this.presignAndUpload(initRes.requestId, transactionId, frontFile, backFile)),
      switchMap(({ requestId }) => this.validateProcess(requestId, transactionId)),
      map((validateRes) => this.mapValidateResponse(validateRes, 'DOCUMENT')),
      catchError((error) => of(this.mapErrorToResult(error))),
    );
  }

  initDocumentProcess(transactionId: string): Observable<BioProcessInitResponse> {
    return this.initProcess(transactionId);
  }

  validateCapturedDocument(
    requestId: string,
    transactionId: string,
    frontFile: File,
    backFile: File,
  ): Observable<BioDocumentValidationResult> {
    return this.presignAndUpload(requestId, transactionId, frontFile, backFile).pipe(
      switchMap(({ requestId: uploadedRequestId }) => this.validateProcess(uploadedRequestId, transactionId)),
      map((validateRes) => this.mapValidateResponse(validateRes, 'DOCUMENT')),
      catchError((error) => of(this.mapErrorToResult(error))),
    );
  }

  startLiveprobe(transactionId: string): Observable<BioProcessInitResponse> {
    this.clearPresignedCache();
    return this.post<BioProcessInitResponse>('api/biometric/init-process', transactionId, { type: 'LIVEPROBE' }).pipe(
      catchError((error) => throwError(() => this.isProcessExpired(error) ? this.mapErrorToResult(error, true) : error)),
    );
  }

  validateSelfieCaptures(
    initResponse: BioProcessInitResponse,
    captures: BioSelfieCapture[],
    transactionId: string,
  ): Observable<BioDocumentValidationResult> {
    const imageTypes = captures.map((capture) => capture.imageType);
    return forkJoin(captures.map((capture) => this.computeMd5(capture.file))).pipe(
      switchMap((md5Values) => {
        const request$ = this.post<BioPresignedUrlResponse>('api/biometric/presigned-url', transactionId, {
          requestId: initResponse.requestId,
          files: captures.map((capture, index) => ({
            fileName: capture.file.name,
            image_type: capture.imageType,
            contentType: capture.file.type,
            md5: md5Values[index],
          })),
        });
        return this.getOrRequestPresignedUrl(initResponse.requestId, imageTypes, request$).pipe(
          map((response) => ({ response, md5Values })),
          catchError((error) => throwError(() => this.isProcessExpired(error) ? error : { error: 'PRESIGNED_URL_ERROR' })),
        );
      }),
      switchMap(({ response, md5Values }) => {
        const urls = response.presignedUrls || [];
        const uploads = captures.map((capture, index) => {
          const url = urls.find((entry) => entry.image_type === capture.imageType)?.url;
          return url
            ? this.uploadBioDocument(capture.file, url, md5Values[index]).pipe(
              catchError((error) => throwError(() => this.isProcessExpired(error) ? error : { error: 'UPLOAD_ERROR' })),
            )
            : throwError(() => ({ error: 'PRESIGNED_URL_ERROR' }));
        });
        return urls.length > 0
          ? forkJoin(uploads).pipe(map(() => initResponse.requestId))
          : throwError(() => ({ error: 'PRESIGNED_URL_ERROR' }));
      }),
      switchMap((requestId) => this.validateProcess(requestId, transactionId).pipe(
        catchError((error) => throwError(() => this.isProcessExpired(error) ? error : { error: 'VALIDATE_PROCESS_ERROR' })),
      )),
      map((response) => this.mapValidateResponse(response, 'SELFIE')),
      catchError((error) => of(this.mapErrorToResult(error, true))),
    );
  }

  private initProcess(transactionId: string): Observable<BioProcessInitResponse> {
    return this.captchaSrv.getCaptcha(this.captchaAction).pipe(
      switchMap((captchaToken) =>
        this.post<BioProcessInitResponse>('api/biometric/init-process', transactionId, {
          type: 'DOCUMENT',
          captchaToken,
        }),
      ),
    );
  }

  private presignAndUpload(
    requestId: string,
    transactionId: string,
    frontFile: File,
    backFile: File,
  ): Observable<{ requestId: string }> {
    const imageTypes: BioProcessImageType[] = ['DOCUMENT_FRONT', 'DOCUMENT_BACK'];
    return forkJoin({
      front: this.computeMd5(frontFile),
      back: this.computeMd5(backFile),
    }).pipe(
      switchMap(({ front: frontMd5, back: backMd5 }) => {
        const request$ = this.captchaSrv.getCaptcha(this.captchaAction).pipe(
          switchMap((captchaToken) =>
            this.post<BioPresignedUrlResponse>('api/biometric/presigned-url', transactionId, {
              requestId,
              files: [
                {
                  fileName: frontFile.name,
                  image_type: 'DOCUMENT_FRONT' as BioProcessImageType,
                  contentType: frontFile.type,
                  md5: frontMd5,
                },
                {
                  fileName: backFile.name,
                  image_type: 'DOCUMENT_BACK' as BioProcessImageType,
                  contentType: backFile.type,
                  md5: backMd5,
                },
              ],
              captchaToken,
            }),
          ),
        );
        return this.getOrRequestPresignedUrl(requestId, imageTypes, request$).pipe(
          map((presignedRes) => ({ presignedRes, frontMd5, backMd5 })),
        );
      }),
      switchMap(({ presignedRes, frontMd5, backMd5 }) =>
        this.uploadToPresignedUrls(presignedRes, frontFile, backFile, frontMd5, backMd5),
      ),
      map(() => ({ requestId })),
    );
  }

  private uploadToPresignedUrls(
    presignedRes: BioPresignedUrlResponse,
    frontFile: File,
    backFile: File,
    frontMd5: string,
    backMd5: string,
  ): Observable<void> {
    if (!presignedRes?.requestId || !Array.isArray(presignedRes.presignedUrls)) {
      return throwError(() => ({ error: 'INVALID_REQUEST', message: 'Faltan URLs de carga del DNI.' }));
    }
    const frontEntry = presignedRes.presignedUrls.find((entry) => entry.image_type === 'DOCUMENT_FRONT');
    const backEntry = presignedRes.presignedUrls.find((entry) => entry.image_type === 'DOCUMENT_BACK');
    if (!frontEntry?.url || !backEntry?.url) {
      return throwError(() => ({ error: 'INVALID_REQUEST', message: 'Faltan URLs de carga del DNI.' }));
    }
    return forkJoin({
      front: this.uploadBioDocument(frontFile, frontEntry.url, frontMd5),
      back: this.uploadBioDocument(backFile, backEntry.url, backMd5),
    }).pipe(map(() => undefined));
  }

  private uploadBioDocument(file: File, presignedUrl: string, md5: string): Observable<void> {
    return this.putToS3(presignedUrl, file, md5);
  }

  private putToS3(url: string, file: File, md5: string): Observable<void> {
    return new Observable<void>((subscriber) => {
      fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream',
          'Content-MD5': md5,
        },
        body: file,
      })
        .then((response) => {
          if (!response.ok) {
            subscriber.error({ error: 'INTERNAL_ERROR', message: 'No pudimos subir la foto del DNI.' });
            return;
          }
          subscriber.next();
          subscriber.complete();
        })
        .catch(() => subscriber.error({ error: 'INTERNAL_ERROR', message: 'No pudimos subir la foto del DNI.' }));
    });
  }

  private validateProcess(requestId: string, transactionId: string): Observable<BioValidateResponse> {
    return this.post<BioValidateResponse>('api/biometric/validate-process', transactionId, { requestId });
  }

  private getOrRequestPresignedUrl(
    requestId: string,
    imageTypes: string[],
    request$: Observable<BioPresignedUrlResponse>,
  ): Observable<BioPresignedUrlResponse> {
    const cached = this.getCachedPresigned(requestId, imageTypes);
    if (cached) {
      return of(cached);
    }
    return request$.pipe(
      map((response) => {
        this.setCachedPresigned(requestId, imageTypes, response);
        return response;
      }),
      catchError((error) => throwError(() => this.isProcessExpired(error) && !(error instanceof HttpErrorResponse)
        ? { ...(error as BioApiErrorBody), errorPhase: 'PRESIGNED_URL' } : error)),
    );
  }

  private buildPresignedCacheKey(requestId: string, imageTypes: string[]): string {
    return `${requestId}::${[...imageTypes].sort().join(',')}`;
  }

  private getCachedPresigned(requestId: string, imageTypes: string[]): BioPresignedUrlResponse | null {
    return this.presignedCache.get(this.buildPresignedCacheKey(requestId, imageTypes)) ?? null;
  }

  private setCachedPresigned(requestId: string, imageTypes: string[], response: BioPresignedUrlResponse): void {
    this.presignedCache.set(this.buildPresignedCacheKey(requestId, imageTypes), response);
  }

  clearPresignedCache(requestId?: string, imageTypes?: string[]): void {
    if (!requestId) {
      this.presignedCache.clear();
      return;
    }
    if (imageTypes) {
      this.presignedCache.delete(this.buildPresignedCacheKey(requestId, imageTypes));
      return;
    }
    const prefix = `${requestId}::`;
    Array.from(this.presignedCache.keys())
      .filter((key) => key.startsWith(prefix))
      .forEach((key) => this.presignedCache.delete(key));
  }

  private mapValidateResponse(response: BioValidateResponse, step: 'DOCUMENT' | 'SELFIE'): BioDocumentValidationResult {
    const status = response.status as BioProcessStatus;
    const biometric = {
      requestId: response.requestId,
      ...(response.customerBiometricId != null && { customerBiometricId: response.customerBiometricId }),
    };
    if (status === 'finish_ok' || (step === 'DOCUMENT' && status === 'complete')) {
      return { success: true, status, message: '', ...biometric };
    }
    if (status === 'finish_fail') {
      const ux = BIO_ERROR_UX.FINAL_FAILED;
      return { success: false, status, errorKind: 'FINAL_FAILED', title: ux.title, message: ux.message, ...biometric };
    }
    const failureReason = response.failureReason;
    const isImageFailure = !!failureReason && FAILURE_REASON_TO_IMAGE_KIND.has(failureReason);
    const kindByStatus: Record<Exclude<BioProcessStatus, 'finish_ok' | 'finish_fail'>, BioErrorKind> = {
      complete: 'TECHNICAL',
      failed: step === 'SELFIE' ? 'RETRY' : isImageFailure ? 'IMAGE' : 'DOCUMENT',
      retry: 'RETRY',
    };
    const kind = kindByStatus[status] ?? 'TECHNICAL';
    const ux = BIO_ERROR_UX[kind];
    return { success: false, status, errorKind: kind, title: ux.title, message: ux.message, ...biometric };
  }

  private mapErrorToResult(error: unknown, liveprobe = false): BioDocumentValidationResult {
    const httpError = error as HttpErrorResponse;
    const body = (httpError?.error ?? error) as (BioApiErrorBody & { errorPhase?: 'PRESIGNED_URL' }) | undefined;
    const code = this.isProcessExpired(error) && (!(error instanceof HttpErrorResponse) || liveprobe)
      ? 'PROCESS_EXPIRED' : body?.error;
    const kind = (code && ERROR_CODE_TO_KIND[code]) || HTTP_STATUS_TO_KIND[httpError?.status] || 'TECHNICAL';
    const ux = BIO_ERROR_UX[kind];
    const errorPhase = kind === 'PRESIGNED_URL_ERROR' || (kind === 'PROCESS_EXPIRED' && body?.errorPhase === 'PRESIGNED_URL') ? 'PRESIGNED_URL' :
      kind === 'UPLOAD_ERROR' ? 'UPLOAD' :
      kind === 'VALIDATE_PROCESS_ERROR' ? 'VALIDATE_PROCESS' : undefined;
    return {
      success: false, errorKind: kind, errorPhase, title: ux.title,
      message: liveprobe && error instanceof HttpErrorResponse && this.isProcessExpired(error) && typeof body?.message === 'string'
        ? body.message : ux.message,
    };
  }

  private isProcessExpired(error: unknown): boolean {
    const httpError = error as HttpErrorResponse;
    const body = (httpError?.error ?? error) as { code?: string; error?: string } | undefined;
    return body?.code === 'PROCESS_EXPIRED' || body?.error === 'PROCESS_EXPIRED';
  }

  private computeMd5(_file: File): Observable<string> {
    return from(computeContentMd5(_file));
  }

  private post<T>(endpoint: string, transactionId: string, body: object): Observable<T> {
    const interceptorAddsCountryId = this.bioConfig.baseUrl === environment.webApiUrl;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      ...(interceptorAddsCountryId ? {} : { 'X-Country-Id': this.bioConfig.countryId }),
      'X-Transaction-Id': transactionId,
    });
    return this.http.post<T>(`${this.bioConfig.baseUrl}/${endpoint}`, body, { headers }).pipe(
      map((response) => {
        if (this.isProcessExpired(response)) {
          throw response;
        }
        return response;
      }),
      catchError((error: HttpErrorResponse) => throwError(() => error)),
    );
  }
}
